// src/components/infografia/modals/QRSelectorModal.jsx
import React, { useState } from 'react';
import { FaTimes, FaQrcode, FaLink, FaStore, FaUserPlus, FaSearch, FaTicketAlt } from 'react-icons/fa';

const QRSelectorModal = ({ isOpen, onClose, onSelectQR, marcaId, marca }) => {
    const [selectedCategory, setSelectedCategory] = useState('publicPages');

    // Obtener la URL base del frontend
    const getBaseUrl = () => {
        return window.location.origin;
    };

    // QRs de páginas públicas
    const publicPagesQRs = [
        {
            id: 'find-ticket',
            name: 'Buscar Tarjeta',
            description: 'QR para que los clientes busquen su tarjeta de puntos por email',
            icon: FaTicketAlt,
            url: `${getBaseUrl()}/find-ticket/${marcaId}`,
            category: 'publicPages'
        },
        {
            id: 'productos',
            name: 'Catálogo de Productos',
            description: 'QR para acceder al catálogo público de productos',
            icon: FaStore,
            url: `${getBaseUrl()}/productos/${marcaId}`,
            category: 'publicPages'
        }
    ];

    // QRs de registro (necesitan tiendaId)
    const registerQRs = marca?.tiendas?.map((tiendaId, index) => ({
        id: `qr-register-${tiendaId}`,
        name: `Registro Club - Tienda ${index + 1}`,
        description: 'QR para que los clientes se registren en el club de fidelización',
        icon: FaUserPlus,
        url: `${getBaseUrl()}/qr-register/${tiendaId}`,
        category: 'register',
        tiendaId: tiendaId
    })) || [];

    // QRs personalizados (URL manual)
    const customQR = {
        id: 'custom',
        name: 'URL Personalizada',
        description: 'Ingresa cualquier URL para generar un QR',
        icon: FaLink,
        category: 'custom'
    };

    const allQRs = [
        ...publicPagesQRs,
        ...registerQRs,
        customQR
    ];

    const filteredQRs = selectedCategory === 'all'
        ? allQRs
        : allQRs.filter(qr => qr.category === selectedCategory);

    const handleSelectQR = (qr) => {
        if (qr.id === 'custom') {
            // Abrir input para URL personalizada
            const url = prompt('Ingresa la URL para el QR Code:');
            if (url && url.trim()) {
                onSelectQR({
                    url: url.trim(),
                    name: 'QR Personalizado'
                });
                onClose();
            }
        } else {
            onSelectQR({
                url: qr.url,
                name: qr.name
            });
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <FaQrcode />
                        Seleccionar QR Code
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FaTimes className="w-5 h-5" />
                    </button>
                </div>

                {/* Categories */}
                <div className="p-4 border-b border-gray-200">
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => setSelectedCategory('publicPages')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedCategory === 'publicPages'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Páginas Públicas
                        </button>
                        {registerQRs.length > 0 && (
                            <button
                                onClick={() => setSelectedCategory('register')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedCategory === 'register'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Registro Club ({registerQRs.length})
                            </button>
                        )}
                        <button
                            onClick={() => setSelectedCategory('custom')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedCategory === 'custom'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            URL Personalizada
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {filteredQRs.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No hay QRs disponibles en esta categoría</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredQRs.map((qr) => {
                                const Icon = qr.icon || FaQrcode;
                                return (
                                    <button
                                        key={qr.id}
                                        onClick={() => handleSelectQR(qr)}
                                        className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                                                <Icon className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-gray-900 mb-1">
                                                    {qr.name}
                                                </h3>
                                                <p className="text-sm text-gray-600 mb-2">
                                                    {qr.description}
                                                </p>
                                                {qr.url && (
                                                    <p className="text-xs text-gray-400 truncate" title={qr.url}>
                                                        {qr.url}
                                                    </p>
                                                )}
                                            </div>
                                            <FaQrcode className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <p className="text-xs text-gray-500 text-center">
                        Selecciona un QR para añadirlo automáticamente al canvas
                    </p>
                </div>
            </div>
        </div>
    );
};

export default QRSelectorModal;

